import { createContext, useContext } from 'react';
import PropTypes from 'prop-types';

/**
 * WidgetModeContext (#828) — per-instance widget mode.
 *
 * ## Зачем
 *
 * `config.WIDGET_MODE` из `runtimeConfig.js` читает `window.runtimeConfig.VITE_WIDGET_MODE`
 * — это ГЛОБАЛЬНАЯ переменная, которая перезаписывается каждым `B2BDemo.mount()` вызовом.
 * Когда на одной странице несколько embed'ов (типично: chat-only в footer +
 * mini-catalog на главной), последний mount выигрывает гонку и все embed'ы начинают
 * читать НЕ свой mode.
 *
 * Симптом: в mini-catalog embed'е периодически появляется filter panel / sort /
 * counter — CarsPage думает что isMiniCatalog=false потому что window.runtimeConfig
 * перезаписан chat-only-embed'ом.
 *
 * ## Решение
 *
 * Каждый mount оборачивает своё React-дерево в `<WidgetModeProvider mode={widgetMode}>`,
 * и консьюмеры (CarsPage, etc) читают mode через `useWidgetMode()` — per-instance,
 * без global singleton.
 */
const WidgetModeContext = createContext(/** @type {string | null} */ (null));

/**
 * Обёртка-провайдер. Пропущенное `mode` должно точно совпадать с `widgetMode`
 * из widget.jsx (один из: 'full' | 'chat-only' | 'catalog-only' | 'mini-catalog').
 */
export function WidgetModeProvider({ mode, children }) {
  return (
    <WidgetModeContext.Provider value={mode}>
      {children}
    </WidgetModeContext.Provider>
  );
}

WidgetModeProvider.propTypes = {
  mode: PropTypes.oneOf(['full', 'chat-only', 'catalog-only', 'mini-catalog']).isRequired,
  children: PropTypes.node,
};

/**
 * Хук чтения текущего widget mode (per-instance, без global race).
 *
 * Fallback: если компонент рендерится ВНЕ Provider (e.g. standalone dev mode,
 * tests, или рефакторинговая ошибка), возвращает 'full' — это не ломает
 * логику, просто все mode-зависимые флаги (isMiniCatalog, etc.) становятся
 * false, как и должно быть для full mode.
 *
 * В dev-сборке выводит console.warn при отсутствии Provider'а — чтобы поймать
 * пропущенное обёртывание на этапе разработки, а не в проде. В production
 * warning подавлен (не хотим шуметь в консолях клиентских сайтов).
 */
export function useWidgetMode() {
  const mode = useContext(WidgetModeContext);
  if (mode == null && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      '[useWidgetMode] No WidgetModeProvider found in React tree. ' +
      'Falling back to "full". If this is a widget embed, ensure the tree ' +
      'is wrapped with <WidgetModeProvider mode={widgetMode}> — обычно это ' +
      'делает registry-компонент (Catalog / Chat) автоматически.',
    );
  }
  return mode ?? 'full';
}
